import React, { useState, useEffect, useCallback } from "react";
import { useVersionedApi } from "../utils/versionedApi";
import { useAuth } from "../AuthContext.jsx";
import { UserManagementContext } from "./UserManagementContext.js";

import { safeStorage } from '../utils/safeStorage';
export function UserManagementProvider({ children }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = safeStorage.getItem("evv_token");
  const { user, loading: authLoading } = useAuth();
  const apiClient = useVersionedApi('v1');
  // Fetch users based on role permissions
  const fetchUsers = useCallback(async (page = 1, limit = 50) => {
    if (!token || !user) return;

    // GET /users is org-scoped. Platform admins (super_admin with no organizationId)
    // have no tenant context, so the request always 401s — skip it.
    if (!user.organizationId) return;

    // Only fetch when the user actually holds view_users — the backend enforces
    // that permission (GET /users). Gating on role==='admin' alone is wrong:
    // limited admin roles (e.g. Billing Administrator) are role 'admin' but lack
    // view_users, so the request 403s and spams the console on every page load.
    const canViewUsers = user.role === 'super_admin' || (user.resolvedPermissions || []).includes('view_users');
    if (!canViewUsers) return;

    setLoading(true);
    setError(null);
    try {
      const params = { page, limit };
      
      // Regular admins can only see caregivers
      if (user.role === 'admin') {
        params.role = 'caregiver';
      } else {
        // Super admin sees all users
        params.role = '';
      }
      
      const res = await apiClient.get(`/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      // Handle both old format (direct array) and new format (with pagination)
      const userData = res.data.users || res.data;
      setUsers(userData);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setError("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [token, user, apiClient]);

  // Update a user's role (super_admin can update all, admin can update providers only)
  const updateUserRole = async (userId, newRole) => {
    if (user?.role !== 'super_admin' && user?.role !== 'admin') return;
    
    // Regular admins can only manage providers
    if (user?.role === 'admin' && newRole !== 'provider') return;
    
    try {
      await apiClient.patch(
        `/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchUsers();
    } catch {
      setError("Failed to update user role");
    }
  };

  // Assign a custom role (super_admin only)
  const assignCustomRole = async (userId, customRoleId) => {
    if (user?.role !== 'super_admin') return;
    try {
      const updatedUser = await apiClient.patch(
        `/users/${userId}/custom-role`,
        { customRoleId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update the user in the local state immediately for universal sync
      setUsers((prev) => prev.map(u => u.id === userId ? { ...u, ...updatedUser.data } : u));
      await fetchUsers(); // Also re-fetch to ensure backend and frontend are in sync
    } catch {
      setError("Failed to assign custom role");
    }
  };

  // Assign direct permissions (super_admin only)
  const assignPermissions = async (userId, permissionIds) => {
    if (user?.role !== 'super_admin') return;
    try {
      await apiClient.patch(
        `/users/${userId}/permissions`,
        { permissionIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchUsers();
    } catch {
      setError("Failed to assign permissions");
    }
  };

  useEffect(() => {
    // Fetch users based on role: super_admin sees all, admin sees caregivers only
    if (!token || authLoading || !user) return;
    const canViewUsers = user.role === 'super_admin' || (user.resolvedPermissions || []).includes('view_users');
    if (!canViewUsers) return;
    fetchUsers();
  }, [fetchUsers, token, user, authLoading]);

  return (
    <UserManagementContext.Provider
      value={{
        users,
        loading,
        error,
        fetchUsers,
        updateUserRole,
        assignCustomRole,
        assignPermissions,
        setUsers, // for instant local update if needed
      }}
    >
      {children}
    </UserManagementContext.Provider>
  );
}
